import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useNavUserInformationQuery } from 'api';
import { getLoggedIn, getCurrentUserId } from 'lib/auth';
import mixpanel from 'lib/analytics/mixpanel';
import { useDidMount } from 'lib/hooks/useDidMount';
import type { User } from 'api';

export interface AuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: User | null;
  userIsMember: boolean;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): React.ReactNode {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const isMounted = useDidMount();

  // Determine initial auth state
  useEffect(() => {
    const loggedIn = getLoggedIn();
    setIsAuthenticated(loggedIn);

    if (loggedIn) {
      const userId = getCurrentUserId();
      // Identifies the User so Mixpanel can keep track of them
      mixpanel.identify(userId);
      // We use Register Once to set this as a super property
      mixpanel.register({
        signed_in: true
      });
    }
  }, []);

  // Fetch user data if authenticated
  const { loading: userLoading, data } = useNavUserInformationQuery({
    ssr: true,
    skip: !isAuthenticated
  });

  const authState = useMemo((): AuthState => {
    // During SSR or before mounting, we're still determining auth state
    if (!isMounted) {
      return {
        isLoading: true,
        isAuthenticated: false,
        user: null,
        userIsMember: false
      };
    }

    // If authenticated but still loading user data
    if (isAuthenticated && userLoading) {
      return {
        isLoading: true,
        isAuthenticated: true,
        user: null,
        userIsMember: false
      };
    }

    // Auth state is fully determined
    const user = data?.viewer;
    return {
      isLoading: false,
      isAuthenticated,
      user: user as User,
      userIsMember: Boolean(user?.isMember)
    };
  }, [isMounted, isAuthenticated, userLoading, data]);

  return (
    <AuthContext.Provider value={authState}>{children}</AuthContext.Provider>
  );
}
